/**
 * Traducao do bloqueio CONSECUTIVE_HOURS_LIMIT_EXCEEDED para mensagens pt-BR completas:
 * por que bloqueou, quais plantoes entram na contagem e quando o medico podera voltar.
 * Espelha o padrao de src/modules/company/Doctors/helpers/inviteErrors.ts.
 */
import { format, parseISO } from 'date-fns'
import { formatDateTimeWithTimezone } from '@/utils/date'
import { hasApiErrorCode, unwrapApiError } from '@/utils/apiError'
import type {
  ConsecutiveHoursContributingStation,
  ConsecutiveHoursErrorBody,
  ConsecutiveHoursViolation
} from '@/Network/Types/Responses/ConsecutiveHoursResponses'

export const CONSECUTIVE_HOURS_ERROR_CODE = 'CONSECUTIVE_HOURS_LIMIT_EXCEEDED'

type ConsecutiveHoursErrorLike = {
  response?: {
    data?: ConsecutiveHoursErrorBody
  }
}

/**
 * Devolve as violações quando o erro é o bloqueio da regra; `null` para qualquer outro erro.
 * Detecção via `hasApiErrorCode`, que cobre `data.code`/`data.errorCode` e o fallback
 * legado do código cru em `data.message` enquanto backends antigos (pré-mudança de
 * contrato) ainda não enviam `code`.
 */
export function extractConsecutiveHoursViolations(
  error: unknown
): ConsecutiveHoursViolation[] | null {
  // unwrap: StationService.update normaliza via handleError — o body do axios
  // (com `violations`) fica no originalError do ServiceError.
  const data = (unwrapApiError(error) as ConsecutiveHoursErrorLike)?.response?.data
  if (!data) return null
  if (!hasApiErrorCode(error, CONSECUTIVE_HOURS_ERROR_CODE)) return null
  return Array.isArray(data.violations) ? data.violations : []
}

export function formatViolationDate(value: string): string {
  try {
    const parsed = parseISO(value)
    if (Number.isNaN(parsed.getTime())) return value
    return format(parsed, 'dd/MM/yyyy')
  } catch {
    return value
  }
}

export function formatContributingStation(station: ConsecutiveHoursContributingStation): string {
  // O DTO nao carrega data de fim: `date` e a data de inicio e as horas vem em HH:mm.
  // Quando o fim nao e posterior ao inicio, o plantao termina no dia seguinte — sem o
  // marcador, um plantao de 24h (08:00—08:00) aparece como se durasse zero hora.
  const crossesMidnight = station.endHour <= station.startHour
  const range = `${station.startHour}—${station.endHour}${crossesMidnight ? ' (+1 dia)' : ''}`
  return `${formatViolationDate(station.date)} · ${range} · ${station.scheduleName}`
}

// Diz de QUAL escala vem a regra e separa jornada (span, com gaps curtos) de horas
// trabalhadas: a contagem é por unidade e o toggle é por escala, então sem isso o
// gestor desliga o toggle na escala errada e não fecha a conta (incidente Sumaré,
// 31/08/2026). Backend antigo (sem `ruleScheduleName`) cai no texto anterior.
export function formatViolationHeadline(violation: ConsecutiveHoursViolation): string {
  const rule = violation.ruleScheduleName?.trim()
  if (!rule) {
    // Este branch nunca acrescenta a frase de escopo: `ourTeamOnly` e `ruleScheduleName`
    // foram lançados juntos, então um backend que envia um sempre envia o outro.
    return (
      `Dr(a). ${violation.doctorName} ficaria com ${violation.totalHours}h consecutivas em ` +
      `${violation.healthUnitName}. O limite desta escala é de ${violation.maxHours}h, ` +
      `com pausa mínima de ${violation.minRestHours}h.`
    )
  }
  const worked =
    typeof violation.workedHours === 'number' && violation.workedHours !== violation.totalHours
      ? ` (${violation.workedHours}h trabalhadas)`
      : ''
  return (
    `Dr(a). ${violation.doctorName} ficaria com ${violation.totalHours}h de jornada ` +
    `consecutiva${worked} em ${violation.healthUnitName}. A regra é da escala ${rule}: ` +
    `limite de ${violation.maxHours}h, com pausa mínima de ${violation.minRestHours}h.` +
    (violation.ourTeamOnly ? ' Contabilizando só plantões como equipe interna.' : '')
  )
}

export function formatAvailableAt(violation: ConsecutiveHoursViolation): string {
  if (!violation.availableAt) return ''
  return `Poderá assumir novos plantões nesta unidade a partir de ${formatDateTimeWithTimezone(
    violation.availableAt
  )}.`
}
